import { adToBs, bsToAd, isValidBSDate } from '../utils/converter';

type BSDate = { year: number; month: number; day: number };

const nepaliDigits = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];

function toNepaliNum(num: number | string): string {
  return String(num).replace(/\d/g, (d) => nepaliDigits[parseInt(d, 10)]);
}

const EN_MONTHS = [
  'Baisakh', 'Jestha', 'Ashadh', 'Shrawan', 'Bhadra', 'Ashwin',
  'Kartik', 'Mangsir', 'Poush', 'Magh', 'Falgun', 'Chaitra'
];

const NEP_MONTH_NAMES = [
  'वैशाख', 'जेठ', 'असार', 'साउन', 'भदौ', 'असोज',
  'कार्तिक', 'मंसिर', 'पुष', 'माघ', 'फागुन', 'चैत'
];

const NEP_WEEKDAYS = [
  'आइतवार', 'सोमवार', 'मङ्गलवार', 'बुधवार', 'बिहिवार', 'शुक्रवार', 'शनिवार'
];

const NEP_WEEKDAYS_SHORT = [
  'आइत', 'सोम', 'मङ्गल', 'बुध', 'बिहि', 'शुक', 'शनि'
];

const EN_WEEKDAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

const NEPAL_OFFSET_MS = (5 * 60 + 45) * 60 * 1000;

function resolveBSDate(date: Date | string | number | BSDate): BSDate {
  if (date instanceof Date) {
    return adToBs(date);
  }
  if (typeof date === 'number') {
    return adToBs(date);
  }
  if (typeof date === 'string') {
    const parts = date.split(/[-/.]/);
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      if (isValidBSDate(year, month, day)) {
        return { year, month, day };
      }
    }
    const adDate = new Date(date);
    if (!isNaN(adDate.getTime())) {
      return adToBs(adDate);
    }
    throw new Error(`Invalid date input: "${date}"`);
  }
  if (typeof date === 'object' && date !== null && 'year' in date && 'month' in date && 'day' in date) {
    if (!isValidBSDate(date.year, date.month, date.day)) {
      throw new Error(`Invalid BS date: ${date.year}-${date.month}-${date.day}`);
    }
    return date;
  }
  throw new Error('Invalid date type');
}

/**
 * Formats a Bikram Sambat (BS) date into a string based on the provided format pattern.
 * Supports both English and Devanagari tokens.
 *
 * Tokens:
 * - YYYY: English Year (e.g., 2080)
 * - yyyy: Devanagari Year (e.g., २०८०)
 * - YY: English 2-digit Year (e.g., 80)
 * - yy: Devanagari 2-digit Year (e.g., ८०)
 * - MMMM: Nepali Month Name (e.g., वैशाख)
 * - MMME: Transliterated English Month Name (e.g., Baisakh)
 * - MM: English 2-digit Month (e.g., 01)
 * - mm: Devanagari 2-digit Month (e.g., ०१)
 * - M: English Month (e.g., 1)
 * - m: Devanagari Month (e.g., १)
 * - DD: English 2-digit Day (e.g., 15)
 * - dd: Devanagari 2-digit Day (e.g., १५)
 * - D: English Day (e.g., 5)
 * - d: Devanagari Day (e.g., ५)
 * - dddd: Nepali Weekday Name (e.g., शनिवार)
 * - ddd: Nepali Weekday Short Name (e.g., शनि)
 * - ddde: English Weekday Name (e.g., Saturday)
 */
export function formatBS(dateInput: Date | string | number | BSDate, formatStr: string = 'YYYY-MM-DD'): string {
  const bsDate = resolveBSDate(dateInput);
  const adDate = bsToAd(bsDate);
  const nepalTimeMs = adDate.getTime() + NEPAL_OFFSET_MS;
  const dayOfWeek = new Date(nepalTimeMs).getUTCDay();

  const yearStr = String(bsDate.year);
  const monthStr = String(bsDate.month);
  const dayStr = String(bsDate.day);

  const replacements: Record<string, string> = {
    'YYYY': yearStr,
    'YY': yearStr.slice(-2),
    'yyyy': toNepaliNum(yearStr),
    'yy': toNepaliNum(yearStr.slice(-2)),
    'MMMM': NEP_MONTH_NAMES[bsDate.month - 1],
    'MMME': EN_MONTHS[bsDate.month - 1],
    'MM': monthStr.padStart(2, '0'),
    'M': monthStr,
    'mm': toNepaliNum(monthStr.padStart(2, '0')),
    'm': toNepaliNum(monthStr),
    'DD': dayStr.padStart(2, '0'),
    'D': dayStr,
    'dd': toNepaliNum(dayStr.padStart(2, '0')),
    'd': toNepaliNum(dayStr),
    'dddd': NEP_WEEKDAYS[dayOfWeek],
    'ddd': NEP_WEEKDAYS_SHORT[dayOfWeek],
    'ddde': EN_WEEKDAYS[dayOfWeek]
  };

  const regex = /ddde|dddd|MMMM|MMME|yyyy|YYYY|ddd|yy|mm|dd|YY|MM|DD|m|d|M|D/g;
  return formatStr.replace(regex, (match) => replacements[match]);
}
